import log from "loglevel";

import { API_BASE_URL } from "@/env";
import { User, TokenResponse, OAuthConfig } from "@/api/types";

import { router } from "@/router";

// utility functions for case conversion between snake_case and camelCase
import { camelCase, snakeCase } from "case-anything";
import { error } from "@/utils/toast";
import { getCookie } from "@/utils/cookies";
import { fetchEventSource, EventSourceMessage } from "@microsoft/fetch-event-source";

function transformKeys(obj: any, fn: (key: string) => string): any {
  if (Array.isArray(obj)) {
    return obj.map((v) => transformKeys(v, fn));
  } else if (obj && typeof obj === "object" && obj.constructor === Object) {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [fn(k), transformKeys(v, fn)])
    );
  }
  return obj;
}

type ClientRequestOptions = {
  sendAuth?: boolean; // whether to send the Authorization header
  requireAuth?: boolean; // whether to attempt re-authentication on 401 responses
  retryLogin?: boolean; // whether to redirect to login on 401 responses if re-authentication fails
  recaseInputFields?: boolean; // whether to convert input fields to snake_case before sending
  recaseOutputFields?: boolean; // whether to convert output fields to camelCase after receiving response
};

const defaultClientRequestOptions: ClientRequestOptions = {
  sendAuth: true,
  requireAuth: true,
  retryLogin: true,
  recaseInputFields: true,
  recaseOutputFields: true,
};

class ApiClient {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  /**
   * Performs a request against the ClarifAI API.
   * @template T the expected response type
   * @param endpoint the API endpoint to call
   * @param options additional options for the request, such as method and body
   * @param clientReqOptions options for client request handling, such as authentication and recasing
   *
   * This method handles adding the necessary headers, including the Authorization header if a token is present.
   * It also handles token refresh logic if a 401 Unauthorized response is received.
   *
   * @throws Error if the request fails or if the token refresh fails
   *
   * @example
   * const data = await apiClient.get<Project[]>('/projects');
   * @returns Promise<T> the parsed JSON response from the API
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    clientReqOpts: ClientRequestOptions = {},
    emptyResponseExpected: boolean = false
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;

    // fold the defaultClientRequestOptions into the supplied clientReqOpts
    clientReqOpts = { ...defaultClientRequestOptions, ...clientReqOpts };
    
    const headers: Record<string, string> = {
        ...(options.headers as Record<string, string>),
    };

    const csrftoken = getCookie("csrftoken");

    // for non-form data, we want to set the content type and recase input fields
    if (!(options.body instanceof FormData)) {
      // if body is provided, convert its keys to snake_case
      if (clientReqOpts.recaseInputFields && options.body) {
        log.trace("Recasing input fields to snake_case");
        options.body = transformKeys(options.body, snakeCase);
      }
      // add default headers for JSON requests if not already present
      headers["Content-Type"] = headers["Content-Type"] || "application/json";
      headers["Accept"] = headers["Accept"] || "application/json";
      if (csrftoken) {
        headers["X-CSRFToken"] = csrftoken;
      }

      // ensure that options.body is stringified prior to submission if it's not
      if (options.body && typeof options.body === "object") {
        options.body = JSON.stringify(options.body);
      }
    }

    // send auth headers if requested
    // (fyi, if your route doesn't require auth and you send bad credentials
    // it'll still fail, in which case you should set sendAuth to false)
    if (clientReqOpts.sendAuth) {
      const token = localStorage.getItem("access_token");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }

    const config: RequestInit = {
      ...options,
      headers,
      credentials: "include", // include cookies for CSRF token
    };

    // hold the response from the server, or 'null' if any error occurred
    let response;

    try {
      response = await fetch(url, config);
    }
    catch (exc) {
      response = null;
      log.error("Caught error during API request:", exc);
    }

    if (!response || !response.ok) {
      if (clientReqOpts.requireAuth && (response == null || response.status === 401)) {
        // first, try to refresh the token
        const refreshToken = localStorage.getItem('refresh_token');
        
        if (refreshToken) {
          log.debug('Token expired, attempting to refresh...');

          try {
            const newTokens = await this.refreshToken(refreshToken);

            log.debug('Token refreshed successfully:', newTokens);
            localStorage.setItem('access_token', newTokens.access_token);
            localStorage.setItem('refresh_token', newTokens.refresh_token);

            // Retry the original request with the new token
            return this.request<T>(endpoint, options, clientReqOpts);
          } catch (error) {
            console.error('Failed to refresh token:', error);
          }
        }
        else {
          log.warn('No refresh token available, redirecting to login');
        }

        // Token expired, try to refresh or logout
        localStorage.removeItem("access_token");
        localStorage.removeItem("refresh_token");

        // redirect to login page via the router
        if (clientReqOpts.retryLogin) {
          router.navigate("/login", { state: { next: window.location.pathname } });
        }
      }

      const errorPayload = await response
        .json()
        .catch((exc) => ({ error: "Request failed", exc: exc }));

      if (errorPayload) {
        throw new Error(errorPayload.error, { cause: errorPayload });
      }
      else {
        throw new Error(`HTTP ${response.status}`);
      }
    }

    // if it's a 204 No Content response, return an empty object
    if (emptyResponseExpected) {
      if (response.status === 204) {
        return {} as T;
      }
    }

    if (clientReqOpts.recaseOutputFields) {
      log.trace("Recasing output fields to camelCase");
      // if recaseOutputFields is true, convert the response keys to camelCase
      return response
        .json()
        .then((data) => transformKeys(data, camelCase)) as Promise<T>;
    }

    return response
      .json()
  }

  // Auth endpoints
  async getCurrentUser(): Promise<User> {
    return this.request<User>("/current-user/", {}, { requireAuth: true, retryLogin: true });
  }

  async getOAuthConfig(): Promise<OAuthConfig> {
    return this.request("/oauth-config/", {}, {
      sendAuth: false, requireAuth: false, retryLogin: false, recaseOutputFields: false
    });
  }

  // token conversions, management
  async convertGoogleToken(
    oauthConfig: OAuthConfig | null,
    googleCredential: string,
  ): Promise<TokenResponse> {
    const body = new URLSearchParams({
      token: googleCredential,
      backend: "google-oauth2",
      grant_type: "convert_token",
      client_id: oauthConfig?.django_client_id!,
    });

    const response = await fetch(`${API_BASE_URL}/auth/convert-token/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRFToken": getCookie("csrftoken") || "",
      },
      credentials: "include",
      body: body.toString(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.error_description || error.error || "Token conversion failed",
      );
    }

    // cache the client id for future requests
    if (oauthConfig) {
      localStorage.setItem("django_client_id", oauthConfig.django_client_id);
    }

    return response.json();
  }

  async loginWithPassword(
    usernameOrEmail: string,
    password: string,
    oauthConfig: OAuthConfig | null = null,
  ): Promise<TokenResponse> {
    // pull the client id from localStorage first; if missing, use oauth config
    let clientId = localStorage.getItem("django_client_id");
    if (!clientId) {
      if (!oauthConfig) {
        oauthConfig = await this.getOAuthConfig();
      }
      clientId = oauthConfig.django_client_id;
    }

    if (!clientId) {
      throw new Error("Django OAuth client ID is not configured");
    }

    const body = new URLSearchParams({
      grant_type: "password",
      username: usernameOrEmail,
      password,
      client_id: clientId,
    });

    const response = await fetch(`${API_BASE_URL}/auth/token/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
        "X-CSRFToken": getCookie("csrftoken") || "",
      },
      credentials: "include",
      body: body.toString(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error_description || error.error || "Login failed");
    }

    localStorage.setItem("django_client_id", clientId);
    return response.json();
  }

  /**
   * Ensures that the access token is fresh by attempting to refresh it, if a
   * refresh token is available.
   *
   * Since GCP sends us back an opaque token, we can't check its expiration
   * time; instead, we just always attempt to refresh it and replace both the
   * access and refresh tokens in localStorage with new ones.
   *
   * @returns Promise<{ success: boolean; tokens?: TokenResponse; reason?:
   * string }>
   */
  async ensureTokenIsFresh() {
      // first, try to refresh the token
      const refreshToken = localStorage.getItem('refresh_token');
      
      if (refreshToken) {
        log.debug('Token expired, attempting to refresh...');

        try {
          const newTokens = await this.refreshToken(refreshToken);

          log.debug('Token refreshed successfully:', newTokens);
          localStorage.setItem('access_token', newTokens.access_token);
          localStorage.setItem('refresh_token', newTokens.refresh_token);

          return { success: true, tokens: newTokens };
        } catch (error) {
          console.error('Failed to refresh token:', error);
          return { success: false, reason: String(error) };
        }
      }
      else {
        log.warn('No refresh token available');
        return { success: false, reason: "no token available" };
      }
  }

  async refreshToken(refreshToken: string, oauthConfig: OAuthConfig | null = null): Promise<TokenResponse> {
    // attempt to pull the client id from localStorage first
    // if not present, use the oauthConfig provided
    // if neither is available, fetch the oauthConfig from the API
    let clientId = localStorage.getItem("django_client_id");
    if (!clientId) {
      if (!oauthConfig) {
        oauthConfig = await this.getOAuthConfig();
      }
      clientId = oauthConfig.django_client_id;
    }

    const body = new URLSearchParams({
        grant_type: "refresh_token",
        refresh_token: refreshToken,
        client_id: clientId
    });

    const response = await fetch(`${API_BASE_URL}/auth/token/`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: body.toString(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(
        error.error_description || error.error || "Token refresh failed",
      );
    }

    return response.json();
  }

  // Generic methods for other API calls

  /**
   * Performs a GET request to the specified endpoint under the ClarifAI API.
   *
   * @template T the expected response type
   * @param endpoint the API endpoint to call
   * @param requireAuth whether the request requires authentication (default: true)
   * @returns Promise<T> the parsed JSON response from the API, w/payload of type T
   */
  async get<T>(
    endpoint: string,
    clientReqOpts: ClientRequestOptions = {}
  ): Promise<T> {
    return this.request<T>(endpoint, {}, clientReqOpts);
  }

  /**
   * Performs a POST request to the specified endpoint under the ClarifAI API.
   *
   * POSTs are used to create new resources or otherwise trigger stateful actions.
   *
   * @template T the expected response type
   * @param endpoint the API endpoint to call
   * @returns Promise<T> the parsed JSON response from the API, w/payload of type T
   */
  async post<T>(endpoint: string, data: any, clientReqOpts: ClientRequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      method: "POST",
      body: data,
    }, clientReqOpts);
  }

  /**
   * Performs a PUT request to the specified endpoint under the ClarifAI API.
   *
   * PUTs are generally used to update existing resources.
   *
   * @template T the expected response type
   * @param endpoint the API endpoint to call
   * @returns Promise<T> the parsed JSON response from the API, w/payload of type T
   */
  async put<T>(endpoint: string, data: any, clientReqOpts: ClientRequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PUT",
      body: data,
    }, clientReqOpts);
  }

  /**
   * Performs a PATCH request to the specified endpoint under the ClarifAI API.
   *
   * PATCHes are generally used to partially update existing resources.
   *
   * @template T the expected response type
   * @param endpoint the API endpoint to call
   * @returns Promise<T> the parsed JSON response from the API, w/payload of type T
   */
  async patch<T>(endpoint: string, data: any, clientReqOpts: ClientRequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      method: "PATCH",
      body: data,
    }, clientReqOpts);
  }

  /**
   * Performs a DELETE request to the specified endpoint under the ClarifAI API.
   *
   * @template T the expected response type
   * @param endpoint the API endpoint to call
   * @returns Promise<T> the parsed JSON response from the API, w/payload of type T
   */
  async delete<T>(endpoint: string, clientReqOpts: ClientRequestOptions = {}): Promise<T> {
    return this.request<T>(endpoint, {
      method: "DELETE",
    }, clientReqOpts, true);
  }

  /**
   * Initiates a Server-Sent Events (SSE) stream to the specified endpoint with authentication.
   * 
   * @param endpoint the API endpoint to stream from
   * @param onMessage callback function to handle incoming messages
   * @param onError optional callback function to handle errors
   * @param onOpen optional callback function to handle connection open
   * @param clientReqOpts options for client request handling
   * @returns Promise<AbortController> that resolves with an AbortController to close the stream
   * 
   * @example
   * const controller = await apiClient.stream('/events', (message) => {
   *   console.log('Received:', message.data);
   *   if (message.data === 'close') {
   *     controller.abort(); // Close the stream
   *   }
   * });
   * 
   * // Or close it later
   * setTimeout(() => controller.abort(), 10000);
   */
  async stream(
    endpoint: string,
    onMessage: (message: EventSourceMessage, controller: AbortController) => void,
    onError?: (error: any) => void,
    onOpen?: (response: Response) => Promise<void>,
    clientReqOpts: ClientRequestOptions = {}
  ): Promise<AbortController> {
    const url = `${this.baseURL}${endpoint}`;
    
    // fold the defaultClientRequestOptions into the supplied clientReqOpts
    clientReqOpts = { ...defaultClientRequestOptions, ...clientReqOpts };

    console.log("Starting SSE stream to", url);
    console.log("Client request options:", clientReqOpts);
    
    // Create an AbortController to control the stream
    const controller = new AbortController();
    
    const headers: Record<string, string> = {};
    
    // Add authentication headers if requested
    if (clientReqOpts.sendAuth) {
      const token = localStorage.getItem("access_token");
      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }
    }
    
    // Add CSRF token for SSE requests
    const csrftoken = getCookie("csrftoken");
    if (csrftoken) {
      headers["X-CSRFToken"] = csrftoken;
    }

    // Set up the fetchEventSource options
    const fetchOptions = {
      method: "GET",
      headers,
      credentials: "include" as RequestCredentials,
      signal: controller.signal,
      onmessage: (message: EventSourceMessage) => {
        // Pass the controller to the onMessage callback so it can close the stream
        onMessage(message, controller);
      },
      onerror: (error: any) => {
        log.error("SSE connection error:", error);
        
        // Handle 401 errors with token refresh logic
        if (clientReqOpts.requireAuth && error.status === 401) {
          const refreshToken = localStorage.getItem('refresh_token');
          
          if (refreshToken) {
            log.debug('SSE token expired, attempting to refresh...');
            
            this.refreshToken(refreshToken)
              .then((newTokens) => {
                log.debug('Token refreshed successfully for SSE:', newTokens);
                localStorage.setItem('access_token', newTokens.access_token);
                localStorage.setItem('refresh_token', newTokens.refresh_token);
                
                // Note: The SSE connection will need to be re-established by the caller
                // with the new token
              })
              .catch((refreshError) => {
                console.error('Failed to refresh token for SSE:', refreshError);
                localStorage.removeItem("access_token");
                localStorage.removeItem("refresh_token");
                
                if (clientReqOpts.retryLogin) {
                  router.navigate("/login", { state: { next: window.location.pathname } });
                }
              });
          } else {
            log.warn('No refresh token available for SSE, redirecting to login');
            localStorage.removeItem("access_token");
            localStorage.removeItem("refresh_token");
            
            if (clientReqOpts.retryLogin) {
              router.navigate("/login", { state: { next: window.location.pathname } });
            }
          }
        }
        
        if (onError) {
          onError(error);
        }
      },
      onopen: onOpen,
    };

    // Start the SSE stream (this is async but we don't await it)
    fetchEventSource(url, fetchOptions);
    
    // Return the controller immediately so the caller can abort the stream
    return controller;
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
